// ./src/contexts/TrackingStorageContext/TrackingStorageContext.tsx
//
// Persistent storage abstraction for GPS tracking data.
// Stores two things:
//   1. Point buffer — filtered GPS points not yet uploaded to the server
//   2. Session state — travelId, seq counter, gpsDistance, duration, etc.
//
// Uses IndexedDB in v1. The ITrackingStorage interface allows swapping
// to Capacitor native storage later without changing consumers.
//
// Architecture:
// - ITrackingStorage interface defines the contract
// - IndexedDBTrackingStorage implements it for browser environments
// - InMemoryTrackingStorage provides a fallback when IndexedDB is unavailable
// - TrackingStorageProvider wraps the active implementation in a React context
// - useTrackingStorage() hook for consumers

import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from "react";

// ============================================
// Types
// ============================================

/**
 * A GPS point that has passed the client-side filter and is buffered
 * for upload. Stored in IndexedDB between flush cycles for crash resilience.
 */
export interface BufferedTrackingPoint {
  lat: number;
  lng: number;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  accuracy: number | null;
  /** ISO 8601 timestamp of the GPS reading on the device */
  recordedAt: string;
  /** Monotonically increasing sequence number (global per travel) */
  seq: number;
  /** Segment counter (incremented on each resume after pause) */
  segmentId: number;
}

/**
 * Persisted tracking session state. Restored on app restart to
 * seamlessly continue an in-progress tracking session.
 */
export interface TrackingSessionState {
  /** The travel record being tracked */
  travelId: string;
  /** Client-side session status */
  status: "active" | "paused";
  /** Current segment counter (from server, incremented on resume) */
  segmentId: number;
  /** Next seq number to assign to the next filtered point */
  nextSeq: number;
  /** Running GPS distance in km (sent with each batch upload) */
  gpsDistance: number;
  /** Seconds of active tracking time (excludes pauses) */
  activeDuration: number;
  /**
   * Timestamp (ms since epoch) of when active tracking last ran.
   * Used to calculate elapsed time on app restart:
   *   elapsed = now - lastActiveTimestamp
   * Only updated while status is 'active'.
   */
  lastActiveTimestamp: number;
  /**
   * The last raw GPS reading's lat/lng, used by the distance
   * accumulator to calculate distance on the next reading after restart.
   */
  lastRawLat: number | null;
  lastRawLng: number | null;
}

/**
 * Storage interface contract. All methods are async to support
 * both IndexedDB (async) and Capacitor native storage (async).
 */
export interface ITrackingStorage {
  // ── Point buffer ──
  /** Get all buffered points waiting for upload */
  getBuffer(): Promise<BufferedTrackingPoint[]>;
  /** Replace the entire buffer (called after filtering new points or partial flush) */
  setBuffer(points: BufferedTrackingPoint[]): Promise<void>;
  /** Clear the buffer (called after successful upload or discard) */
  clearBuffer(): Promise<void>;

  // ── Session state ──
  /** Get the persisted session state, or null if no session */
  getSessionState(): Promise<TrackingSessionState | null>;
  /** Save/overwrite the session state */
  setSessionState(state: TrackingSessionState): Promise<void>;
  /** Clear the session state (called on complete or discard) */
  clearSessionState(): Promise<void>;

  /** Clear everything — buffer + session. Used on discard or logout. */
  clearAll(): Promise<void>;
}

// ============================================
// IndexedDB Implementation
// ============================================

/** Database name — unique to this feature to avoid conflicts */
const DB_NAME = "carexpenses_tracking";
/** Database version — increment if object store schema changes */
const DB_VERSION = 1;
/** Object store names */
const STORE_BUFFER = "buffer";
const STORE_SESSION = "session";
/**
 * Single key used in each store. We overwrite the same key on every save
 * rather than using auto-increment, keeping the stores simple.
 */
const SINGLE_KEY = "current";

/**
 * IndexedDB-backed implementation of ITrackingStorage.
 *
 * Uses two object stores, each with a single key that gets overwritten:
 * - "buffer" store → key "current" → BufferedTrackingPoint[]
 * - "session" store → key "current" → TrackingSessionState
 *
 * All operations are wrapped in try/catch. If IndexedDB throws
 * (e.g., private browsing quota exceeded), the error is logged
 * but not rethrown — the caller continues with in-memory data.
 */
class IndexedDBTrackingStorage implements ITrackingStorage {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Open (or reuse) the IndexedDB database connection.
   * Creates object stores on first open or version upgrade.
   */
  private getDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_BUFFER)) {
          db.createObjectStore(STORE_BUFFER);
        }
        if (!db.objectStoreNames.contains(STORE_SESSION)) {
          db.createObjectStore(STORE_SESSION);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.warn(
          "[TrackingStorage] IndexedDB open failed:",
          request.error
        );
        reject(request.error);
      };
    });

    // If the open itself fails, clear the cached promise so next call retries
    this.dbPromise.catch(() => {
      this.dbPromise = null;
    });

    return this.dbPromise;
  }

  /**
   * Generic get: read a value from a store by key.
   */
  private async get<T>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await this.getDb();
      return new Promise<T | null>((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => resolve((request.result as T) ?? null);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn(`[TrackingStorage] get(${storeName}) failed:`, err);
      return null;
    }
  }

  /**
   * Generic put: write a value to a store at a key (overwrite if exists).
   */
  private async put<T>(
    storeName: string,
    key: string,
    value: T
  ): Promise<void> {
    try {
      const db = await this.getDb();
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        const request = store.put(value, key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn(`[TrackingStorage] put(${storeName}) failed:`, err);
    }
  }

  /**
   * Generic delete: remove a key from a store.
   */
  private async delete(storeName: string, key: string): Promise<void> {
    try {
      const db = await this.getDb();
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn(`[TrackingStorage] delete(${storeName}) failed:`, err);
    }
  }

  // ── Buffer operations ──

  async getBuffer(): Promise<BufferedTrackingPoint[]> {
    const result = await this.get<BufferedTrackingPoint[]>(
      STORE_BUFFER,
      SINGLE_KEY
    );
    return result ?? [];
  }

  async setBuffer(points: BufferedTrackingPoint[]): Promise<void> {
    await this.put(STORE_BUFFER, SINGLE_KEY, points);
  }

  async clearBuffer(): Promise<void> {
    await this.delete(STORE_BUFFER, SINGLE_KEY);
  }

  // ── Session state operations ──

  async getSessionState(): Promise<TrackingSessionState | null> {
    return this.get<TrackingSessionState>(STORE_SESSION, SINGLE_KEY);
  }

  async setSessionState(state: TrackingSessionState): Promise<void> {
    await this.put(STORE_SESSION, SINGLE_KEY, state);
  }

  async clearSessionState(): Promise<void> {
    await this.delete(STORE_SESSION, SINGLE_KEY);
  }

  // ── Bulk clear ──

  async clearAll(): Promise<void> {
    await this.clearBuffer();
    await this.clearSessionState();
  }
}

// ============================================
// In-Memory Fallback Implementation
// ============================================

/**
 * In-memory fallback when IndexedDB is unavailable (e.g., some private
 * browsing modes, very old browsers, or SSR environments).
 *
 * Data survives within the page session but is lost on tab close.
 * A console warning is logged once when this fallback is used.
 */
class InMemoryTrackingStorage implements ITrackingStorage {
  private buffer: BufferedTrackingPoint[] = [];
  private sessionState: TrackingSessionState | null = null;

  constructor() {
    console.warn(
      "[TrackingStorage] IndexedDB unavailable — using in-memory fallback. " +
      "Tracking data will not survive app restart."
    );
  }

  async getBuffer(): Promise<BufferedTrackingPoint[]> {
    return [...this.buffer];
  }
  async setBuffer(points: BufferedTrackingPoint[]): Promise<void> {
    this.buffer = [...points];
  }
  async clearBuffer(): Promise<void> {
    this.buffer = [];
  }

  async getSessionState(): Promise<TrackingSessionState | null> {
    return this.sessionState ? { ...this.sessionState } : null;
  }
  async setSessionState(state: TrackingSessionState): Promise<void> {
    this.sessionState = { ...state };
  }
  async clearSessionState(): Promise<void> {
    this.sessionState = null;
  }

  async clearAll(): Promise<void> {
    this.buffer = [];
    this.sessionState = null;
  }
}

// ============================================
// Factory
// ============================================

/**
 * Detect the best available storage implementation.
 *
 * Priority:
 * 1. (Future) Capacitor native storage — when running as a native app
 * 2. IndexedDB — standard browser implementation
 * 3. In-memory fallback — when IndexedDB is unavailable
 */
const createTrackingStorage = (): ITrackingStorage => {
  // Future: detect Capacitor environment
  // if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
  //   return new CapacitorTrackingStorage();
  // }

  // Check IndexedDB availability
  if (typeof indexedDB !== "undefined") {
    return new IndexedDBTrackingStorage();
  }

  return new InMemoryTrackingStorage();
};

// ============================================
// React Context
// ============================================

const TrackingStorageContext = createContext<ITrackingStorage | null>(null);

export interface TrackingStorageProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that makes the tracking storage instance available to
 * all child components via useTrackingStorage().
 *
 * Should be placed in the component tree above any tracking-related
 * components but below the authentication provider (so we can clear
 * storage on logout).
 *
 * The storage instance is created once on mount and never recreated.
 */
export const TrackingStorageProvider: React.FC<
  TrackingStorageProviderProps
> = ({ children }) => {
  const storageRef = useRef<ITrackingStorage | null>(null);

  // Create storage instance once, lazily
  const storage = useMemo(() => {
    if (!storageRef.current) {
      storageRef.current = createTrackingStorage();
    }
    return storageRef.current;
  }, []);

  // Cleanup: clear storage when the provider unmounts (e.g., logout)
  // This prevents stale data from a previous user's session
  useEffect(() => {
    return () => {
      // Don't clear on unmount — the session might still be active
      // and we want crash resilience. Clearing is explicit via clearAll().
    };
  }, []);

  return (
    <TrackingStorageContext.Provider value={storage}>
      {children}
    </TrackingStorageContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Access the tracking storage instance.
 *
 * Must be used within a TrackingStorageProvider.
 *
 * @example
 * ```tsx
 * const storage = useTrackingStorage();
 *
 * // Save filtered points
 * await storage.setBuffer(filteredPoints);
 *
 * // Restore on app restart
 * const buffer = await storage.getBuffer();
 * const session = await storage.getSessionState();
 *
 * if (session) {
 *   // Resume tracking from where we left off
 *   console.log(`Restoring session for travel ${session.travelId}`);
 * }
 *
 * // Clean up on discard or complete
 * await storage.clearAll();
 * ```
 */
export const useTrackingStorage = (): ITrackingStorage => {
  const storage = useContext(TrackingStorageContext);

  if (!storage) {
    throw new Error(
      "useTrackingStorage must be used within a TrackingStorageProvider"
    );
  }

  return storage;
};